/**
 * Generic validation utilities for form fields
 * Can be reused across different form components
 */

import { EVENT_INSTANCE_TYPES, RECURRENCE, RECURRENCE_V2 } from 'settings/constants/service'
import { isWeeklyRecurrence, isDailyRecurrence, isYearlyRecurrence } from 'utils/service'
import T from 'T'

/**
 * Helper function to generate pluralized validation messages
 * @param {number} count - Number of items
 * @param {string} action - Action to perform (e.g., 'select', 'unselect')
 * @param {string} itemName - Name of items being selected
 * @returns {string} Formatted message
 */
const getMessage = (count, action, itemName) => {
  const plural = count === 1 ? '' : 's'
  return `Please ${action} ${count} ${itemName}${plural}`
}

/**
 * Generic validation for checkbox groups with flexible count requirements
 * @param {Object} selectedCount - Number of selected options
 * @param {number} requiredCount - Number of required selections
 * @param {string} itemName - Name of items being selected (for error messages)
 * @param {Object} options - Validation options
 * @param {boolean} options.exactCount - If true, validates exact count; if false, validates minimum count (default: false)
 * @returns {Object} Validation result with isValid and message
 */
export const validateSelections = (selectedCount = 0, requiredCount = 1, itemName = 'item', options = {}) => {
  const { exactCount = false } = options
  const remainingCount = requiredCount - selectedCount

  let isValid
  let message = null

  if (exactCount) {
    // Exact count validation - selected count must equal required count
    isValid = selectedCount === requiredCount

    if (selectedCount < requiredCount) {
      message = getMessage(remainingCount, 'select', itemName)
    } else if (selectedCount > requiredCount) {
      const excessCount = selectedCount - requiredCount
      message = getMessage(excessCount, 'unselect', itemName)
    }
  } else {
    // Minimum count validation - selected count must be at least required count
    isValid = selectedCount >= requiredCount

    if (!isValid) {
      message = getMessage(remainingCount, 'select', itemName)
    }
  }

  return {
    isValid,
    selectedCount,
    remainingCount,
    message,
  }
}

/**
 * Validates weekly recurrence: number of selected days must match the frequency
 * @param {Object} daysOfWeek - Object with day keys and isSelected boolean values
 * @param {number} frequency - Required number of days to be selected
 * @returns {Object} Validation result with isValid and message
 */
export const validateWeeklyRecurrence = (daysOfWeek = {}, frequency = 1) => {
  // Handle null/undefined daysOfWeek
  if (!daysOfWeek || typeof daysOfWeek !== 'object') {
    daysOfWeek = {}
  }

  const selectedDays = Object.entries(daysOfWeek).filter(([key, value]) => value?.isSelected).length

  return validateSelections(selectedDays, frequency, 'day', { exactCount: true })
}

/**
 * Helper function to check for duplicates in an array
 * @param {Array} array - Array to check for duplicates
 * @param {string} fieldName - Field name for error message
 * @param {string} message - Custom error message
 * @returns {Object|null} Error object if duplicates found, null otherwise
 */
const checkDuplicates = (array, fieldName, message) => {
  if (!Array.isArray(array) || array.some(value => value === undefined || value === null || value === '')) {
    return null // Let individual field validation handle missing/empty values
  }

  const uniqueValues = new Set(array)
  if (array.length !== uniqueValues.size) {
    return {
      isValid: false,
      message,
      errors: { [fieldName]: { message } },
    }
  }
  return null
}

/**
 * Helper function to determine if event occurrence represents a daily interval
 * @param {Array} eventOccurrence - Event occurrence values
 * @returns {boolean} True if daily interval, false otherwise
 */
const isDailyInterval = eventOccurrence => {
  return (
    !Array.isArray(eventOccurrence) ||
    eventOccurrence.length === 0 ||
    eventOccurrence.every(occ => occ === undefined || occ === null || occ === '')
  )
}

/**
 * Helper function to validate day of week duplicates for daily intervals
 * @param {Array} dayOfTheWeek - Day of week values
 * @returns {Object|null} Validation result or null if valid
 */
const validateDailyInterval = dayOfTheWeek => {
  return checkDuplicates(dayOfTheWeek, 'dayOfTheWeek', T.EACH_OCCURRENCE_MUST_BE_UNIQUE)
}

/**
 * Helper function to validate occurrence and day combinations for non-daily intervals
 * @param {Array} eventOccurrence - Event occurrence values
 * @param {Array} dayOfTheWeek - Day of week values
 * @returns {Object|null} Validation result or null if valid
 */
const validateNonDailyInterval = (eventOccurrence, dayOfTheWeek) => {
  if (!Array.isArray(eventOccurrence) || !Array.isArray(dayOfTheWeek)) {
    return null
  }

  const combinations = eventOccurrence.map((occ, idx) => `${occ}-${dayOfTheWeek[idx]}`)
  const duplicateCheck = checkDuplicates(combinations, 'eventOccurrence', T.EACH_OCCURRENCE_MUST_BE_UNIQUE)

  if (duplicateCheck) {
    return {
      ...duplicateCheck,
      errors: {
        eventOccurrence: { message: T.EACH_OCCURRENCE_MUST_BE_UNIQUE },
        dayOfTheWeek: { message: T.EACH_DAY_OF_THE_WEEK_MUST_BE_UNIQUE },
      },
    }
  }

  return null
}

/**
 * Helper function to validate day of week event instances
 * @param {Array} eventOccurrence - Event occurrence values
 * @param {Array} dayOfTheWeek - Day of week values
 * @returns {Object|null} Validation result or null if valid
 */
const validateDayOfWeekInstance = (eventOccurrence, dayOfTheWeek) => {
  if (isDailyInterval(eventOccurrence)) {
    return validateDailyInterval(dayOfTheWeek)
  }

  return validateNonDailyInterval(eventOccurrence, dayOfTheWeek)
}

/**
 * Validates non-weekly recurrence: checks for duplicate combinations
 * @param {string} eventInstanceType - Type of event instance
 * @param {Array} eventOccurrence - Event occurrence values (always array)
 * @param {Array} dayOfTheWeek - Day of week values (always array)
 * @param {Array} dayOfTheMonth - Day of month values (always array)
 * @param {number | string} frequency - Number of occurrences expected
 * @returns {Object} Validation result with isValid, message, and errors object
 */
export const validateNonWeeklyRecurrence = (eventInstanceType, eventOccurrence, dayOfTheWeek, dayOfTheMonth, frequency) => {
  const errors = {}
  const validResult = { isValid: true, message: null, errors }

  // Only validate duplicates for multiple occurrences
  if (frequency && frequency <= 1) {
    return validResult
  }

  if (eventInstanceType === EVENT_INSTANCE_TYPES.DAY_OF_WEEK) {
    const validationResult = validateDayOfWeekInstance(eventOccurrence, dayOfTheWeek)
    if (validationResult) return validationResult
  } else if (eventInstanceType === EVENT_INSTANCE_TYPES.SPECIFIC_DATE) {
    const validationResult = checkDuplicates(
      dayOfTheMonth.map(n => Number(n)),
      'dayOfTheMonth',
      T.EACH_DAY_OF_THE_MONTH_MUST_BE_UNIQUE
    )
    if (validationResult) return validationResult
  }

  return validResult
}

const countFilled = list => {
  const arr = Array.isArray(list) ? list : [list]
  return arr.filter(v => v !== undefined && v !== null && v !== '').length
}

/**
 * CS-2707 pre-publish validator for the AdjustService edit form.
 * `reason` discriminates where the UI should surface the failure:
 * `'startDate'` near the start-date field, `'recurrence'` near the pencil button.
 */
export const validateRecurrenceForPublish = (formData = {}) => {
  const safeFormData = formData && typeof formData === 'object' ? formData : {}
  const {
    serviceType,
    recurrence,
    startDate,
    recurrenceFrequency,
    daysOfWeek,
    eventInstanceType,
    eventOccurrence,
    dayOfTheWeek,
    dayOfTheMonth,
  } = safeFormData

  const isRecurringService = serviceType === RECURRENCE_V2.RECURRING || serviceType === RECURRENCE.RECURRING

  if (!isRecurringService) {
    return { isValid: true, message: null }
  }

  if (!startDate) {
    return {
      isValid: false,
      message: `${T.PLEASE_SELECT} a ${T.SERVICE_START_DATE.toLowerCase()} before publishing.`,
      reason: 'startDate',
    }
  }

  const interval = recurrence?.recurrenceInterval
  if (!interval) {
    return { isValid: false, message: T.EDIT_RECURRENCE_INSTRUCTION_BEFORE_PUBLISH, reason: 'recurrence' }
  }

  const frequency = Math.max(parseInt(recurrenceFrequency, 10) || 1, 1)

  if (isYearlyRecurrence(interval) || isDailyRecurrence(interval)) {
    return { isValid: true, message: null }
  }

  if (isWeeklyRecurrence(interval)) {
    const weekly = validateWeeklyRecurrence(daysOfWeek || {}, frequency)
    if (!weekly.isValid) {
      return { isValid: false, message: T.EDIT_RECURRENCE_INSTRUCTION_BEFORE_PUBLISH, reason: 'recurrence' }
    }
    return { isValid: true, message: null }
  }

  if (!eventInstanceType) {
    return { isValid: false, message: T.EDIT_RECURRENCE_INSTRUCTION_BEFORE_PUBLISH, reason: 'recurrence' }
  }

  if (eventInstanceType === EVENT_INSTANCE_TYPES.DAY_OF_WEEK) {
    if (countFilled(eventOccurrence) !== frequency || countFilled(dayOfTheWeek) !== frequency) {
      return { isValid: false, message: T.EDIT_RECURRENCE_INSTRUCTION_BEFORE_PUBLISH, reason: 'recurrence' }
    }
    const nonWeekly = validateNonWeeklyRecurrence(eventInstanceType, eventOccurrence, dayOfTheWeek, dayOfTheMonth, frequency)
    if (!nonWeekly.isValid) {
      return { isValid: false, message: T.EDIT_RECURRENCE_INSTRUCTION_BEFORE_PUBLISH, reason: 'recurrence' }
    }
    return { isValid: true, message: null }
  }

  if (eventInstanceType === EVENT_INSTANCE_TYPES.SPECIFIC_DATE) {
    if (countFilled(dayOfTheMonth) !== frequency) {
      return { isValid: false, message: T.EDIT_RECURRENCE_INSTRUCTION_BEFORE_PUBLISH, reason: 'recurrence' }
    }
    const nonWeekly = validateNonWeeklyRecurrence(eventInstanceType, eventOccurrence, dayOfTheWeek, dayOfTheMonth, frequency)
    if (!nonWeekly.isValid) {
      return { isValid: false, message: T.EDIT_RECURRENCE_INSTRUCTION_BEFORE_PUBLISH, reason: 'recurrence' }
    }
    return { isValid: true, message: null }
  }

  return { isValid: true, message: null }
}
